import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import {
  PermitInfoSection,
  CoveredVehiclesSection,
  UploadedDocumentsSection,
  FeeSummarySection,
  TermsConditionsSection,
} from "./sections";
import { ReviewSubmitStepContentProps, FeeSummary, formatRenewalPeriod } from "./types";

export const ReviewSubmitStepContent: React.FC<ReviewSubmitStepContentProps> = ({
  authorityName,
  authorityId,
  permitType,
  vehicles,
  vehiclesLoading,
  documents,
  termsAcceptedAt,
  onTermsChange,
}) => {
  // Derive whether to show fee summary based on payment processing being enabled
  const showFeeSummary = permitType.paymentProcessingEnabledAt !== null;
  const isPerVehicle = permitType.vehicleScope === "per_vehicle";

  // Calculate fee summary if payment processing is enabled
  const feeSummary: FeeSummary | null = useMemo(() => {
    if (!showFeeSummary) {
      return null;
    }

    const vehicleCount = isPerVehicle ? vehicles.length : 1;
    const totalFee = isPerVehicle
      ? permitType.pricePerRenewalPeriod * vehicles.length
      : permitType.pricePerRenewalPeriod;

    return {
      basePrice: permitType.pricePerRenewalPeriod,
      vehicleCount,
      isPerVehicle,
      totalFee,
      renewalPeriodLabel: formatRenewalPeriod(permitType.renewalPeriod),
    };
  }, [permitType, vehicles.length, showFeeSummary, isPerVehicle]);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" component="h2" gutterBottom fontWeight={600}>
          Review & Submit
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Review your application details before submitting
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Permit Information Section */}
        <Grid item xs={12}>
          <PermitInfoSection authorityName={authorityName} permitType={permitType} />
        </Grid>

        {/* Covered Vehicles Section */}
        <Grid item xs={12} md={6}>
          <CoveredVehiclesSection
            vehicleScope={permitType.vehicleScope}
            vehicles={vehicles}
            loading={vehiclesLoading}
          />
        </Grid>

        {/* Uploaded Documents Section */}
        <Grid item xs={12} md={6}>
          <UploadedDocumentsSection documents={documents} />
        </Grid>

        {/* Fee Summary Section - only shown when payment processing is enabled */}
        {feeSummary && (
          <Grid item xs={12} md={6}>
            <FeeSummarySection feeSummary={feeSummary} />
          </Grid>
        )}

        {/* Terms & Conditions Section */}
        <Grid item xs={12}>
          <TermsConditionsSection
            authorityId={authorityId}
            termsAcceptedAt={termsAcceptedAt}
            onTermsChange={onTermsChange}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReviewSubmitStepContent;
